import moment from 'moment';
import { format } from 'date-fns';
import { getI18n } from 'react-i18next';

/**
 * Compare object properties
 * @param objA first object
 * @param objB second object
 * @param prop property to compare in objects
 * @returns boolean value defines does objects properties equal
 */
export const checkObjEqualProp = (objA, objB, prop) =>
  objA?.[prop] === objB?.[prop];

/**
 * Generates classes
 * @param cls static node class
 * @param mods classes object where key is class and value is class condition to apply
 * @param additional additional classes array
 * @returns classes string
 */
export function classNames(cls, mods = {}, additional = []) {
  return [
    cls,
    ...additional.filter(Boolean),
    ...Object.entries(mods)
      .filter(([_, value]) => Boolean(value))
      .map(([className]) => className),
  ].join(' ');
}

/**
 * Generate responsive size
 * @param size responsive size to get
 * @param ratio size equal to the value in 1px
 * @returns responsive size
 */
export const getResponsiveSize = (
  size,
  ratio = 'calc(var(--index) *  0.034614)', // 1px
) => `calc(${ratio} * ${size})`;

/**
 * Generate responsive dimension
 * @param size responsive dimension to get
 * @param view view to calculate dimension for
 * @returns responsive dimension
 */
export const getResponsiveDimension = (size, view = 'desktop') => {
  const vw = window.innerWidth / 100;
  const vh = window.innerHeight / 100;
  let coff = 0.034614;

  switch (view) {
    case 'laptop':
      coff = 0.0558036;
      break;
    case 'mobile':
      coff = 0.081038;
      break;
    default:
      break;
  }
  const ratio = (vw + vh) * coff;
  return ratio * size;
};

/**
 * Get days, hours, minutes diff between date and now
 * @param date date to calculate from
 * @returns string contains days or hours or minutes diff between date and now
 */
export const getDaysHoursMinutesFromDate = (date) => {
  const days = moment(date).diff(Date.now(), 'days');
  if (days)
    return -days === 1
      ? `${-days} ${getI18n().t('time-range-numerals.day')}`
      : `${-days} ${getI18n().t('time-range-numerals.days')}`;

  const hours = moment(date).add(-days, 'days').diff(Date.now(), 'hours');
  if (hours)
    return -hours === 1
      ? `${-hours} ${getI18n().t('time-range-numerals.hour')}`
      : `${-hours} ${getI18n().t('time-range-numerals.hours')}`;

  const minutes = moment(date).diff(Date.now(), 'minutes');
  if (minutes)
    return -minutes === 1
      ? `${-minutes} ${getI18n().t('time-range-numerals.minute')}`
      : `${-minutes} ${getI18n().t('time-range-numerals.minutes')}`;
};

/**
 * Check does table's filter exists and retrun it's trasformed value
 * @param filterList list of filters
 * @param filter looking filter
 * @returns transformed filter value
 */
export const getTransformedLogicTableFilterValue = (filterList, filter) => {
  if (
    filterList?.[filter] &&
    filterList?.[filter]?.sign !== null &&
    filterList?.[filter]?.value !== null
  )
    return JSON.stringify({
      [filterList?.[filter]?.sign]: filterList?.[filter]?.value,
    });
  else return '';
};

/**
 * Encode string for the CSV
 * @returns transformed string
 */
export const encodeForCSV = (str, delimiter = ',', quote = '"') => {
  // 1. Handle newlines within the string: Replace them with a placeholder
  //    that won't interfere with CSV structure.  We'll replace it back later.
  const newlinePlaceholder = '__NEWLINE_PLACEHOLDER__';
  const escapedStr = str?.trim()?.replace(/\n/g, newlinePlaceholder);

  // 2. Replace the newline placeholder back to actual newlines.  Do this *after*
  //    the quoting and escaping to avoid messing up the CSV structure.
  const finalValue = escapedStr?.replace(
    new RegExp(newlinePlaceholder, 'g'),
    '\n',
  );

  return finalValue;
};

export const mapProductLoverCaseNames = (productName) => {
  switch (productName) {
    case 'emdr-remote':
      return 'EMDR-Remote';
    case 'emdr-plus':
      return 'EMDR-Plus';
    case 'emdr-vr':
      return 'EMDR-VR';
    default:
      break;
  }
};

export const mapOrderToSort = (currentSort) => {
  switch (currentSort) {
    case 'up':
      return 'DESC';
    case 'down':
      return 'ASC';
    case 'DESC':
      return 'up';
    case 'ASC':
      return 'down';
    case 'default':
      return '';
    case '':
      return 'default';
  }
};

export const skipArg = (num) => new Array(num);

/**
 * Generates a metadata object with active filters for the intercom event to track
 * @param conmfig table filter's config
 * @param filters active filters
 * @returns transformed metadata obj
 */
export const intercomFormFilterEventDataForTableFilters = (config, filters) => {
  const intercomEventData = {};

  Object.keys(filters).forEach((filterKey) => {
    const excludedFilters = ['organizationId', 'generated_uuid', 'device_name'];
    const filterType = config.find((item) => item.filter === filterKey).type;
    const filter = filters[filterKey];

    if (excludedFilters.includes(filterKey)) return;

    switch (filterType) {
      case 'autocomplete':
        intercomEventData[filterKey] = filter.value;
        break;
      case 'toggle':
        intercomEventData[filterKey] = filter.value;
        break;
      case 'range':
        intercomEventData[
          filterKey
        ] = `${filter.value[0]} to ${filter.value[1]}`;
        break;
      case 'conditional-number':
        intercomEventData[filterKey] = `${filter.sign} ${filter.value}`;
        break;
      case 'conditional-date':
        intercomEventData[filterKey] = `${filter.sign} ${filter.value}`;
        break;
    }
  });

  return intercomEventData;
};

export const getMonthList = (locale) => {
  const year = new Date().getFullYear();
  const monthList = Array.from({ length: 12 }, (_, i) => i);
  const formatter = new Intl.DateTimeFormat(locale?.code, { month: 'long' });

  return monthList.map((monthIndex) => {
    const date = new Date(year, monthIndex);
    return format(date, 'MMMM', { locale });
  });
};
